<template>
  <div class="tab-content account-info-page">
    <header class="page-header">
      <div class="page-header-text">
        <h1>{{ $t('controlCenter.accountInfo.title') }}</h1>
        <p class="page-subtitle">{{ $t('controlCenter.accountInfo.subtitle') }}</p>
      </div>
      <button
        v-if="isLptBrand"
        class="invoice-info-add-btn"
        type="button"
        @click="openAddInvoiceInfo"
      >
        {{ $t('controlCenter.accountInfo.invoiceInfo.addButton') }}
      </button>
    </header>

    <div v-if="loading" class="loading-container">
      <p>{{ $t('controlCenter.common.loading') }}</p>
    </div>

    <div v-else class="page-content">
      <section class="card">
        <header class="card-header">
          <h2 class="card-title">{{ $t('controlCenter.accountInfo.accountDetails') }}</h2>
        </header>
        <div class="card-body">
          <div class="info-grid info-grid--2col">
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.accountId'), accountData.id || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.accountId'), accountData.id || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.accountId') }}</label>
              <span class="info-value">{{ accountData.id || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.accountType'), accountData.type || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.accountType'), accountData.type || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.accountType') }}</label>
              <span class="info-value">{{ accountData.type || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.administrator'), administratorDisplay, $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.administrator'), administratorDisplay, $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.administrator') }}</label>
              <span class="info-value">
                {{ accountData.administrator?.email || '-' }}
                <span v-if="accountData.administrator?.fullName" class="info-value--sub">
                  ({{ accountData.administrator.fullName.firstName }} {{ accountData.administrator.fullName.lastName }})
                </span>
                <span
                  class="admin-change-link"
                  @click.stop="handleChangeAdmin"
                >[{{ $t('controlCenter.accountInfo.change') }}]</span>
              </span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.creationTime'), formatDate(accountData.creationTime), $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.creationTime'), formatDate(accountData.creationTime), $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.creationTime') }}</label>
              <span class="info-value">{{ formatDate(accountData.creationTime) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="card">
        <header class="card-header">
          <h2 class="card-title">{{ $t('controlCenter.accountInfo.organizationInfo') }}</h2>
        </header>
        <div class="card-body">
          <div class="info-grid info-grid--2col">
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.name'), organization.name || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.name'), organization.name || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.name') }}</label>
              <span class="info-value">{{ organization.name || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.legalName'), organization.legalName || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.legalName'), organization.legalName || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.legalName') }}</label>
              <span class="info-value">{{ organization.legalName || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.contactEmail'), organization.contactEmail || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.contactEmail'), organization.contactEmail || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.contactEmail') }}</label>
              <span class="info-value">{{ organization.contactEmail || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.phoneNumber'), organization.phoneNumber || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.phoneNumber'), organization.phoneNumber || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.phoneNumber') }}</label>
              <span class="info-value">{{ organization.phoneNumber || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.industries'), (organization.industries || []).join(', ') || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.industries'), (organization.industries || []).join(', ') || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.industries') }}</label>
              <span class="info-value">{{ (organization.industries || []).join(', ') || '-' }}</span>
            </div>
            <div
              class="info-item info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.taxIdentificationNumber'), organization.taxIdentificationNumber || '-', $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.taxIdentificationNumber'), organization.taxIdentificationNumber || '-', $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.taxIdentificationNumber') }}</label>
              <span class="info-value">{{ organization.taxIdentificationNumber || '-' }}</span>
            </div>
            <div
              class="info-item info-item--span2 info-item--clickable"
              @mousedown="handleMouseDown($event)"
              @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.legalRepresentative'), legalRepDisplay, $event)"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              @touchcancel="handleTouchEnd"
              @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.legalRepresentative'), legalRepDisplay, $event)"
              @contextmenu="handleContextMenu"
            >
              <label class="info-label">{{ $t('controlCenter.accountInfo.organization.legalRepresentative') }}</label>
              <span class="info-value">
                {{ organization.legalRepresentative?.firstName || '' }} {{ organization.legalRepresentative?.lastName || '-' }}
              </span>
            </div>
          </div>

          <div class="group-card">
            <h3 class="group-title">{{ $t('controlCenter.accountInfo.organization.registration') }}</h3>
            <div v-if="registrationLoading" class="group-loading">
              <div class="spinner"></div>
            </div>
            <div v-else class="info-grid info-grid--3col">
              <div
                v-for="field in registrationFields"
                :key="field.key"
                class="info-item info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStart(field, $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @click.stop="handleFieldClick(field, $event)"
                @dblclick.stop="handleFieldCopy(field, $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ field.label }}</label>
                <span class="info-value">{{ field.value || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="group-card">
            <h3 class="group-title">{{ $t('controlCenter.accountInfo.organization.address') }}</h3>
            <div class="info-grid info-grid--2col">
              <div
                class="info-item info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.countryOrRegion'), address.countryOrRegion || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.countryOrRegion'), address.countryOrRegion || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.countryOrRegion') }}</label>
                <span class="info-value">{{ address.countryOrRegion || '-' }}</span>
              </div>
              <div
                class="info-item info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.stateOrProvince'), address.stateOrProvince || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.stateOrProvince'), address.stateOrProvince || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.stateOrProvince') }}</label>
                <span class="info-value">{{ address.stateOrProvince || '-' }}</span>
              </div>
              <div
                class="info-item info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.city'), address.city || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.city'), address.city || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.city') }}</label>
                <span class="info-value">{{ address.city || '-' }}</span>
              </div>
              <div
                class="info-item info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.postalCode'), address.postalCode || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.postalCode'), address.postalCode || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.postalCode') }}</label>
                <span class="info-value">{{ address.postalCode || '-' }}</span>
              </div>
              <div
                class="info-item info-item--span2 info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.addressLine1'), address.addressLine1 || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.addressLine1'), address.addressLine1 || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.addressLine1') }}</label>
                <span class="info-value">{{ address.addressLine1 || '-' }}</span>
              </div>
              <div
                class="info-item info-item--span2 info-item--clickable"
                @mousedown="handleMouseDown($event)"
                @touchstart="handleTouchStartCopy($t('controlCenter.accountInfo.organization.addressLine2'), address.addressLine2 || '-', $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchEnd"
                @dblclick.stop="handleCopy($t('controlCenter.accountInfo.organization.addressLine2'), address.addressLine2 || '-', $event)"
                @contextmenu="handleContextMenu"
              >
                <label class="info-label">{{ $t('controlCenter.accountInfo.organization.addressLine2') }}</label>
                <span class="info-value">{{ address.addressLine2 || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="isLptBrand" class="card invoice-info-card">
        <header class="card-header invoice-info-card__header">
          <h2 class="card-title">{{ $t('controlCenter.accountInfo.invoiceInfo.title') }}</h2>
          <span class="invoice-info-card__count">{{ invoiceInfos.length }}</span>
        </header>
        <div class="card-body">
          <div v-if="invoiceInfoLoading" class="invoice-info-state">
            {{ $t('controlCenter.common.loading') }}
          </div>
          <div v-else-if="!invoiceInfos.length" class="invoice-info-state">
            {{ $t('controlCenter.accountInfo.invoiceInfo.empty') }}
          </div>
          <div v-else class="invoice-info-list">
            <div v-for="item in invoiceInfos" :key="item.id" class="invoice-info-item">
              <div class="invoice-info-item__main">
                <div class="invoice-info-title">{{ item.title }}</div>
                <span v-if="item.defaultInfo" class="invoice-info-default">{{ $t('controlCenter.accountInfo.invoiceInfo.default') }}</span>
                <div class="invoice-info-meta">
                  <span v-if="item.taxId">{{ $t('controlCenter.accountInfo.invoiceInfo.fields.taxId') }}：{{ item.taxId }}</span>
                  <span v-if="item.address">{{ $t('controlCenter.accountInfo.invoiceInfo.fields.address') }}：{{ item.address }}</span>
                  <span v-if="item.phone">{{ $t('controlCenter.accountInfo.invoiceInfo.fields.phone') }}：{{ item.phone }}</span>
                  <span v-if="item.bankName">{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankName') }}：{{ item.bankName }}</span>
                  <span v-if="item.bankAccount">{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAccount') }}：{{ item.bankAccount }}</span>
                  <span v-if="item.bankAddresses && item.bankAddresses.length">
                    {{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAddresses') }}：{{ item.bankAddresses.join('；') }}
                  </span>
                </div>
              </div>
              <div class="invoice-info-item__actions">
                <button type="button" class="invoice-info-action-btn" @click="openEditInvoiceInfo(item)">
                  {{ $t('controlCenter.accountInfo.invoiceInfo.edit') }}
                </button>
                <button v-if="!item.defaultInfo" type="button" class="invoice-info-action-btn" @click="setDefaultInvoiceInfo(item)">
                  {{ $t('controlCenter.accountInfo.invoiceInfo.setDefault') }}
                </button>
                <button type="button" class="invoice-info-action-btn invoice-info-action-btn--danger" @click="deleteInvoiceInfo(item)">
                  {{ $t('controlCenter.accountInfo.invoiceInfo.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="popupVisible"
      class="description-popup"
      :style="popupStyle"
      @click.stop
    >
      <img
        v-if="popupImage"
        :src="popupImage"
        class="popup-image"
        alt="example"
      />
      <div v-if="popupContent" class="popup-body">{{ popupContent }}</div>
    </div>

    <el-dialog
      v-if="isLptBrand"
      :title="editingInvoiceInfoId ? $t('controlCenter.accountInfo.invoiceInfo.dialogTitleEdit') : $t('controlCenter.accountInfo.invoiceInfo.dialogTitleAdd')"
      :visible.sync="invoiceInfoDialogVisible"
      width="560px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form label-position="top" @submit.native.prevent>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.title')" required>
          <el-input v-model="invoiceInfoForm.title" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.title')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.invoiceType')">
          <el-radio-group v-model="invoiceInfoForm.invoiceType">
            <el-radio label="VAT专用发票">增值税专用发票</el-radio>
            <el-radio label="VAT普通发票">增值税普通发票</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.taxId')">
          <el-input v-model="invoiceInfoForm.taxId" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.taxId')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.address')">
          <el-input v-model="invoiceInfoForm.address" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.address')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.phone')">
          <el-input v-model="invoiceInfoForm.phone" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.phone')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.bankName')">
          <el-input v-model="invoiceInfoForm.bankName" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.bankName')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.bankAccount')">
          <el-input v-model="invoiceInfoForm.bankAccount" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.bankAccount')" />
        </el-form-item>
        <el-form-item :label="$t('controlCenter.accountInfo.invoiceInfo.fields.bankAddresses')">
          <div v-for="(addr, index) in invoiceInfoForm.bankAddresses" :key="index" class="invoice-info-bank-address-row">
            <el-input v-model="invoiceInfoForm.bankAddresses[index]" :placeholder="$t('controlCenter.accountInfo.invoiceInfo.fields.bankAddresses')" />
            <el-button v-if="invoiceInfoForm.bankAddresses.length > 1" type="text" @click="removeBankAddress(index)">
              {{ $t('controlCenter.accountInfo.invoiceInfo.delete') }}
            </el-button>
          </div>
          <el-button type="text" @click="addBankAddress">
            + {{ $t('controlCenter.accountInfo.invoiceInfo.addBankAddress') }}
          </el-button>
        </el-form-item>
      </el-form>
      <div slot="footer" class="invoice-info-dialog-footer">
        <el-button @click="invoiceInfoDialogVisible = false">
          {{ $t('controlCenter.accountInfo.invoiceInfo.cancel') }}
        </el-button>
        <el-button type="primary" :loading="invoiceInfoSaving" @click="saveInvoiceInfo">
          {{ $t('controlCenter.accountInfo.invoiceInfo.save') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { accountApi } from '@/api/account'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'AccountInfo',
  data() {
    return {
      loading: true,
      accountData: {},
      registrationTypes: [],
      registrationLoading: true,
      popupVisible: false,
      popupContent: '',
      popupImage: '',
      activePopupKey: '',
      popupStyle: {},
      lastMouseDownTime: 0,
      longPressTimer: null,
      touchStartX: 0,
      touchStartY: 0,
      touchTriggeredCopy: false,
      isTouchSession: false,
      isHoverDevice: window.matchMedia('(hover: hover)').matches,
      invoiceInfos: [],
      invoiceInfoLoading: false,
      invoiceInfoDialogVisible: false,
      invoiceInfoSaving: false,
      editingInvoiceInfoId: null,
      invoiceInfoForm: {
        title: '',
        invoiceType: 'VAT普通发票',
        taxId: '',
        address: '',
        phone: '',
        bankName: '',
        bankAccount: '',
        bankAddresses: ['']
      }
    }
  },
  computed: {
    accountId() {
      const userInfo = getUserInfo()
      return this.accountData?.id || userInfo?.accountId || ''
    },
    userId() {
      return getUserInfo()?.userId || getUserInfo()?.sub || ''
    },
    isLptBrand() {
      return this.$brand && this.$brand.id === 'lpt'
    },
    administratorDisplay() {
      const admin = this.accountData.administrator
      if (!admin?.email) return '-'
      let text = admin.email
      if (admin.fullName) {
        text += ` (${admin.fullName.firstName} ${admin.fullName.lastName})`
      }
      return text
    },
    legalRepDisplay() {
      const rep = this.organization.legalRepresentative
      if (!rep) return '-'
      const first = rep.firstName || ''
      const last = rep.lastName || ''
      return (first + ' ' + last).trim() || '-'
    },
    organization() {
      return this.accountData.organization || {}
    },
    address() {
      return this.organization.address || {}
    },
    registrationTypeConfig() {
      const code = this.organization.registration?.registrationType
      if (!code) return null
      return this.registrationTypes.find(type => type.code === code) || null
    },
    registrationFields() {
      const config = this.registrationTypeConfig

      const typeLabel = this.$t('controlCenter.accountInfo.organization.registrationType')
      const typeValue = config?.name || this.organization.registration?.registrationType || ''
      const typeDesc = config?.description || ''

      const numberLabel = config?.registrationNumberType?.name || this.$t('controlCenter.accountInfo.organization.registrationNumber')
      const numberValue = this.organization.registration?.registrationNumber || ''
      const numberDesc = config?.registrationNumberType?.description || config?.registrationNumberType?.example || ''

      const docLabel = config?.registrationDocumentType?.name || this.$t('controlCenter.accountInfo.organization.registrationDocument')
      const docValue = this.organization.registration?.registrationDocument || ''
      const docDesc = config?.registrationDocumentType?.description || ''
      const docImage = config?.registrationDocumentType?.example || ''

      return [
        { key: 'registrationType', label: typeLabel, value: typeValue, description: typeDesc },
        { key: 'registrationNumber', label: numberLabel, value: numberValue, description: numberDesc },
        { key: 'registrationDocument', label: docLabel, value: docValue, description: docDesc, image: docImage }
      ]
    }
  },
  async created() {
    await this.fetchData()
    if (this.isLptBrand) {
      this.fetchInvoiceInfos()
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.clearTimers()
  },
  watch: {
    '$i18n.locale'() {
      this.loadRegistrationTypes()
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        if (!this.accountId) {
          console.error('Account ID is not available from JWT')
          this.$message.error(this.$t('controlCenter.accountInfo.accountIdError'))
          return
        }
        const response = await accountApi.getAccount(this.accountId)
        this.accountData = response.data || response
        this.loadRegistrationTypes()
      } catch (error) {
        console.error('Failed to fetch account data:', error)
        this.$message.error(this.$t('controlCenter.accountInfo.loadError'))
      } finally {
        this.loading = false
      }
    },
    loadRegistrationTypes() {
      this.registrationLoading = true
      accountApi.getOrganizationRegistrationTypes()
        .then(response => {
          this.registrationTypes = response.data || response
        })
        .catch(error => {
          console.error('Failed to fetch registration types:', error)
        })
        .finally(() => {
          this.registrationLoading = false
        })
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        return new Date(dateString).toLocaleString()
      } catch (e) {
        return dateString
      }
    },
    resetInvoiceInfoForm() {
      this.invoiceInfoForm = {
        title: '',
        invoiceType: 'VAT普通发票',
        taxId: '',
        address: '',
        phone: '',
        bankName: '',
        bankAccount: '',
        bankAddresses: ['']
      }
    },
    async fetchInvoiceInfos() {
      this.invoiceInfoLoading = true
      try {
        const response = await accountApi.getInvoiceInfos(this.userId)
        this.invoiceInfos = response.data || response || []
      } catch (error) {
        console.error('Failed to fetch invoice infos:', error)
        this.$message.error(this.$t('controlCenter.accountInfo.invoiceInfo.loadError'))
      } finally {
        this.invoiceInfoLoading = false
      }
    },
    openAddInvoiceInfo() {
      this.editingInvoiceInfoId = null
      this.resetInvoiceInfoForm()
      this.invoiceInfoDialogVisible = true
    },
    openEditInvoiceInfo(item) {
      this.editingInvoiceInfoId = item.id
      this.invoiceInfoForm = {
        title: item.title || '',
        invoiceType: item.invoiceType || 'VAT普通发票',
        taxId: item.taxId || '',
        address: item.address || '',
        phone: item.phone || '',
        bankName: item.bankName || '',
        bankAccount: item.bankAccount || '',
        bankAddresses: (item.bankAddresses && item.bankAddresses.length ? [...item.bankAddresses] : [''])
      }
      this.invoiceInfoDialogVisible = true
    },
    addBankAddress() {
      this.invoiceInfoForm.bankAddresses.push('')
    },
    removeBankAddress(index) {
      if (this.invoiceInfoForm.bankAddresses.length <= 1) return
      this.invoiceInfoForm.bankAddresses.splice(index, 1)
    },
    async saveInvoiceInfo() {
      if (!this.invoiceInfoForm.title || !this.invoiceInfoForm.title.trim()) {
        this.$message.warning(this.$t('controlCenter.accountInfo.invoiceInfo.titleRequired'))
        return
      }
      const payload = {
        title: this.invoiceInfoForm.title.trim(),
        invoiceType: this.invoiceInfoForm.invoiceType || 'VAT普通发票',
        taxId: this.invoiceInfoForm.taxId.trim(),
        address: this.invoiceInfoForm.address.trim(),
        phone: this.invoiceInfoForm.phone.trim(),
        bankName: this.invoiceInfoForm.bankName.trim(),
        bankAccount: this.invoiceInfoForm.bankAccount.trim(),
        bankAddresses: this.invoiceInfoForm.bankAddresses.map(a => a.trim()).filter(a => a)
      }
      this.invoiceInfoSaving = true
      try {
        if (this.editingInvoiceInfoId) {
          await accountApi.updateInvoiceInfo(this.editingInvoiceInfoId, payload, this.userId)
        } else {
          await accountApi.createInvoiceInfo(payload, this.userId)
        }
        this.$message.success(this.$t('controlCenter.accountInfo.invoiceInfo.saveSuccess'))
        this.invoiceInfoDialogVisible = false
        await this.fetchInvoiceInfos()
      } catch (error) {
        console.error('Failed to save invoice info:', error)
        this.$message.error(this.$t('controlCenter.accountInfo.invoiceInfo.saveFailed'))
      } finally {
        this.invoiceInfoSaving = false
      }
    },
    async deleteInvoiceInfo(item) {
      try {
        await this.$confirm(
          this.$t('controlCenter.accountInfo.invoiceInfo.deleteConfirm'),
          this.$t('controlCenter.accountInfo.invoiceInfo.title'),
          {
            confirmButtonText: this.$t('controlCenter.accountInfo.invoiceInfo.delete'),
            cancelButtonText: this.$t('controlCenter.accountInfo.invoiceInfo.cancel'),
            type: 'warning'
          }
        )
      } catch (e) {
        return
      }
      try {
        await accountApi.deleteInvoiceInfo(item.id, this.userId)
        this.$message.success(this.$t('controlCenter.accountInfo.invoiceInfo.deleteSuccess'))
        await this.fetchInvoiceInfos()
      } catch (error) {
        console.error('Failed to delete invoice info:', error)
        this.$message.error(this.$t('controlCenter.accountInfo.invoiceInfo.deleteFailed'))
      }
    },
    async setDefaultInvoiceInfo(item) {
      try {
        await accountApi.setDefaultInvoiceInfo(item.id, this.userId)
        await this.fetchInvoiceInfos()
      } catch (error) {
        console.error('Failed to set default invoice info:', error)
        this.$message.error(this.$t('controlCenter.accountInfo.invoiceInfo.saveFailed'))
      }
    },

    handleMouseDown(event) {
      if (this.isTouchSession) return
      const now = Date.now()
      if (now - this.lastMouseDownTime < 600) {
        event.preventDefault()
        if (window.getSelection) {
          window.getSelection().removeAllRanges()
        }
      }
      this.lastMouseDownTime = now
    },
    handleFieldClick(field, event) {
      if (this.touchTriggeredCopy) {
        this.touchTriggeredCopy = false
        return
      }
      this.togglePopup(field, event.currentTarget)
    },
    handleCopy(label, value, event) {
      if (this.isTouchSession) return
      if (event && event.preventDefault) {
        event.preventDefault()
      }
      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      }
      this.copyToClipboard(label, value)
    },
    handleFieldCopy(field, event) {
      this.handleCopy(field.label, field.value, event)
    },
    handleContextMenu(event) {
      if (this.isTouchSession) {
        event.preventDefault()
      }
    },
    handleTouchStartCopy(label, value, event) {
      this.isTouchSession = true
      const touch = event.touches[0]
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.touchTriggeredCopy = false
      this.longPressTimer = setTimeout(() => {
        this.touchTriggeredCopy = true
        if (window.getSelection) {
          window.getSelection().removeAllRanges()
        }
        if (navigator.vibrate) {
          navigator.vibrate(50)
        }
        this.copyToClipboard(label, value)
      }, 500)
    },
    handleTouchStart(field, event) {
      this.handleTouchStartCopy(field.label, field.value, event)
    },
    handleTouchMove(event) {
      if (!this.longPressTimer) return
      const touch = event.touches[0]
      const dx = Math.abs(touch.clientX - this.touchStartX)
      const dy = Math.abs(touch.clientY - this.touchStartY)
      if (dx > 10 || dy > 10) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    },
    handleTouchEnd() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
      setTimeout(() => {
        this.isTouchSession = false
      }, 500)
    },

    togglePopup(field, triggerEl) {
      if (this.popupVisible && this.activePopupKey === field.key) {
        this.popupVisible = false
        this.activePopupKey = ''
      } else {
        this.popupContent = field.description
        this.popupImage = field.image || ''
        this.activePopupKey = field.key
        this.popupVisible = true
        this.$nextTick(() => {
          this.calculatePopupPosition(triggerEl)
        })
      }
    },
    calculatePopupPosition(triggerEl) {
      const popupEl = document.querySelector('.description-popup')
      if (!popupEl || !triggerEl) return

      const triggerRect = triggerEl.getBoundingClientRect()
      const popupRect = popupEl.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const gap = 8

      let top = triggerRect.top - popupRect.height - gap
      let left = triggerRect.left

      if (top < 16) {
        top = triggerRect.bottom + gap
      }
      if (left + popupRect.width > viewportWidth - 16) {
        left = viewportWidth - popupRect.width - 16
      }
      if (left < 16) {
        left = 16
      }

      this.popupStyle = {
        top: top + 'px',
        left: left + 'px',
        transform: 'none'
      }
    },
    handleOutsideClick() {
      this.popupVisible = false
      this.activePopupKey = ''
    },

    copyToClipboard(label, value) {
      const text = `${label}: ${value}`
      if (document.execCommand) {
        this.fallbackCopy(text)
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.showCopiedMessage())
          .catch(() => {
            console.error('Clipboard write failed')
            this.$message.warning(this.$t('controlCenter.accountInfo.copyFailed'))
          })
      } else {
        this.$message.warning(this.$t('controlCenter.accountInfo.copyFailed'))
      }
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      textarea.style.fontSize = '16px'
      document.body.appendChild(textarea)
      const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent)
      if (isIOS) {
        const range = document.createRange()
        range.selectNodeContents(textarea)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        textarea.setSelectionRange(0, text.length)
      } else {
        textarea.select()
      }
      let success = false
      try {
        success = document.execCommand('copy')
        if (success) {
          this.showCopiedMessage()
        }
      } catch (e) {
        console.error('Copy failed:', e)
      }
      document.body.removeChild(textarea)
      if (!success) {
        this.$message.warning(this.$t('controlCenter.accountInfo.copyFailed'))
      }
    },
    showCopiedMessage() {
      this.$message.success(this.$t('controlCenter.accountInfo.copied'))
    },
    handleChangeAdmin() {
      this.$message.info(this.$t('controlCenter.accountInfo.changeAdminTip'))
    },

    clearTimers() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    }
  }
}
</script>

<style scoped>
.account-info-page {
  color: #0f172a;
}

.page-header {
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.page-header-text {
  flex: 1;
  min-width: 0;
}

.invoice-info-add-btn {
  flex-shrink: 0;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a7fd7, #0991d3);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.invoice-info-add-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

.page-header h1 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.loading-container {
  text-align: center;
  padding: 50px;
  font-size: 18px;
  color: #64748b;
}

.invoice-info-state {
  padding: 30px 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.invoice-info-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.invoice-info-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.7);
}

.invoice-info-item__main {
  min-width: 0;
  flex: 1;
}

.invoice-info-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.invoice-info-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: 13px;
  color: #475569;
}

.invoice-info-item__actions {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
}

.invoice-info-action-btn {
  padding: 6px 12px;
  border: 1px solid rgba(10, 127, 215, 0.3);
  border-radius: 8px;
  background: #fff;
  color: #0a7fd7;
  font-size: 13px;
  cursor: pointer;
}

.invoice-info-action-btn--danger {
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.invoice-info-bank-address-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.invoice-info-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.page-content {
  width: 100%;
  max-width: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-content > .invoice-info-card {
  order: -1;
  border-color: rgba(10, 127, 215, 0.34);
  box-shadow: 0 18px 38px rgba(10, 127, 215, 0.12);
}

.card {
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.07);
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(10, 127, 215, 0.1);
}

.invoice-info-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f5faff;
}

.invoice-info-card__count {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(10, 127, 215, 0.2);
  border-radius: 999px;
  color: #0a7fd7;
  background: #fff;
  font-size: 12px;
  font-weight: 800;
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-grid {
  display: grid;
  gap: 20px;
}

.info-grid--2col {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-grid--3col {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item--span2 {
  grid-column: span 2;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  word-break: break-all;
}

.info-value--sub {
  font-size: 13px;
  color: #64748b;
  font-weight: 400;
}

.admin-change-link {
  margin-left: 8px;
  color: #0a7fd7;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.admin-change-link:hover {
  text-decoration: underline;
}

.info-item--clickable {
  width: fit-content;
  padding: 8px 14px;
  margin: -8px -14px;
  border-radius: 4px;
}

@media (hover: hover) {
  .info-item--clickable,
  .info-item--clickable .info-label,
  .info-item--clickable .info-value {
    cursor: pointer;
  }

  .info-item--clickable:hover {
    background-color: rgba(10, 127, 215, 0.04);
  }
}

@media (hover: none) {
  .info-item--clickable {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    touch-action: manipulation;
  }

  .info-item--clickable:active {
    outline: 1px solid #0a7fd7;
    outline-offset: 0;
  }
}

.group-card {
  background: rgba(15, 23, 42, 0.03);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(148, 163, 184, 0.1);
}

.group-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.group-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(148, 163, 184, 0.2);
  border-top-color: #0a7fd7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .page-content {
    max-width: 100%;
  }

  .info-grid--2col,
  .info-grid--3col {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .info-item--span2 {
    grid-column: span 1;
  }

  .card-header {
    padding: 16px;
  }

  .card-body {
    padding: 16px;
    gap: 16px;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .card-title {
    font-size: 17px;
  }

  .info-label {
    font-size: 13px;
  }

  .info-value {
    font-size: 15px;
  }

  .info-value--sub {
    font-size: 13px;
  }

  .group-title {
    font-size: 15px;
  }
}
</style>

<style>
.description-popup {
  position: fixed;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 20px 24px;
  max-width: 480px;
  max-height: 60vh;
  overflow-y: auto;
  z-index: 9999;
}

.popup-image {
  display: block;
  width: 320px;
  height: 220px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin: 0 auto 12px;
}

.popup-body {
  font-size: 14px;
  line-height: 1.6;
  color: #0f172a;
  word-break: break-word;
}
</style>
